// Firebase Web SDK initialization (Analytics only).
// Config values come from environment variables — set them in Netlify:
//   REACT_APP_FIREBASE_API_KEY
//   REACT_APP_FIREBASE_AUTH_DOMAIN
//   REACT_APP_FIREBASE_PROJECT_ID
//   REACT_APP_FIREBASE_STORAGE_BUCKET
//   REACT_APP_FIREBASE_MESSAGING_SENDER_ID
//   REACT_APP_FIREBASE_APP_ID
//   REACT_APP_FIREBASE_MEASUREMENT_ID
//
// All of these are PUBLIC web-app values (safe to ship in the bundle) — they
// only identify a project; Firebase enforces access via Security Rules.

import { initializeApp, getApps } from "firebase/app";
import {
  getAnalytics,
  isSupported,
  logEvent as fbLogEvent,
  setUserProperties as fbSetUserProperties,
} from "firebase/analytics";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const hasMinimumConfig = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.appId &&
    firebaseConfig.measurementId
);

let analytics = null;
let initPromise = null;

function initAnalytics() {
  if (initPromise) return initPromise;
  if (!hasMinimumConfig || typeof window === "undefined") {
    initPromise = Promise.resolve(null);
    return initPromise;
  }
  initPromise = (async () => {
    try {
      const supported = await isSupported();
      if (!supported) return null;
      const app = getApps()[0] || initializeApp(firebaseConfig);
      analytics = getAnalytics(app);
      return analytics;
    } catch (err) {
      // Stay silent in production — analytics is non-essential
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[firebase] analytics init failed:", err);
      }
      return null;
    }
  })();
  return initPromise;
}

// Kick off init at import time (non-blocking)
initAnalytics();

export const ANALYTICS_READY = hasMinimumConfig;

/** Fire a GA4 event. Safe no-op if analytics isn't initialized. */
export async function trackEvent(name, params = {}) {
  const a = await initAnalytics();
  if (!a) return;
  try {
    fbLogEvent(a, name, params);
  } catch (_) {
    // swallow — analytics must never break the UI
  }
}

/** Set a sticky user property (e.g. 'theme', 'device_type'). */
export async function setUserProperty(key, value) {
  const a = await initAnalytics();
  if (!a) return;
  try {
    fbSetUserProperties(a, { [key]: value });
  } catch (_) {
    /* noop */
  }
}
